/**
 * Central query-key factory for the core content domains.
 *
 * These key shapes are the single source of truth shared between useQuery
 * calls and the invalidateQueries / prefetch calls that must stay in sync
 * with them. React Query matches keys by value and invalidates by prefix,
 * so the `*Prefix` entries intentionally return shorter arrays for broad
 * invalidation.
 *
 * Inline key arrays elsewhere still match these (matching is by value, not
 * identity); migrate them here as files get touched.
 */

export const programKeys = {
  /** All published programs. Invalidating this also hits every list variant below. */
  all: ['programs'] as const,
  free: ['programs', 'free'] as const,
  /** The /free page: admin-picked samples only (programs.free_sample). */
  freeSamples: ['programs', 'free', 'samples'] as const,
  newest: (limit: number, userId: string | undefined, birthday: string | null | undefined) =>
    ['programs', 'newest', limit, userId, birthday] as const,
  popular: (limit: number, userId: string | undefined, birthday: string | null | undefined) =>
    ['programs', 'popular', limit, userId, birthday] as const,
  suggested: (userId: string | null | undefined, limit: number, birthday: string | null | undefined) =>
    ['programs', 'suggested', userId, limit, birthday] as const,
  byType: (type: string, limit: number | undefined) => ['programs', 'byType', type, limit] as const,
  /** Prefix for invalidating every cached program detail. */
  detailPrefix: ['program'] as const,
  detail: (programId: string | undefined) => ['program', programId] as const,
  access: (userId: string | undefined, programId: string | undefined) =>
    ['programAccess', userId, programId] as const,
};

export const eventKeys = {
  all: ['events'] as const,
  list: (options: unknown) => ['events', options] as const,
  live: ['events', 'live'] as const,
  upcoming: (limit: number) => ['events', 'upcoming', limit] as const,
  lighthouse: (limit: number) => ['events', 'lighthouse', limit] as const,
};

export const journeyKeys = {
  all: ['journeys'] as const,
  detail: (journeyRef: string | undefined) => ['journey', journeyRef] as const,
  enrollment: (journeyRef: string | undefined, userId: string | undefined) =>
    ['journeyEnrollment', journeyRef, userId] as const,
  enrollmentForJourney: (journeyRef: string | undefined) =>
    ['journeyEnrollment', journeyRef] as const,
  enrollmentPrefix: ['journeyEnrollment'] as const,
  enrollments: (userId: string | undefined) => ['journeyEnrollments', userId] as const,
  enrollmentsPrefix: ['journeyEnrollments'] as const,
  purchased: (userId: string | undefined) => ['userPurchasedJourneys', userId] as const,
  purchaseAccess: (userId: string | undefined, journeyId: string | undefined) =>
    ['journeyPurchaseAccess', userId, journeyId] as const,
  purchaseAccessPrefix: ['journeyPurchaseAccess'] as const,
  learnAccess: (userId: string | undefined, journeyId: string | null | undefined) =>
    ['journeyLearnAccess', userId, journeyId] as const,
  learnAccessPrefix: ['journeyLearnAccess'] as const,
};
